/**
 * Names as a rider would say them.
 *
 * Shared rather than owned by the app, because the notifications the worker writes are read on
 * the same phone as the cards and must not spell a station differently.
 */

/** BART writes the airport out in full. Three letters say it, and fit. */
const AIRPORT = /San Francisco International Airport/gi;

/**
 * A station name as the rider would say it.
 *
 * BART's official names carry a second locality after a slash ("Civic Center / UN Plaza") and
 * spell out "Street", which is useful on a map and noise on a card read at a glance. "Street"
 * earns its place only on the numbered stops, where it is what tells 19th from 12th and 16th
 * from 24th. The city only ever trails "Street", so West Oakland keeps its name.
 */
export function stationLabel(name: string): string {
	const base = name.split(" / ")[0].trim().replace(/\s+Street\s+Oakland$/i, " Street").replace(AIRPORT, "SFO Airport");
	return /^\d/.test(base) ? base.replace(/\s+Street$/i, " St") : base.replace(/\s+Street$/i, "");
}

/**
 * A headsign as the rider would say it.
 *
 * BART mostly writes the *line*, in travel order, rather than where the train ends up: a northbound
 * Yellow train is "SFO / SF / Antioch", which reads on a card as though it is going to SFO when SFO
 * is where it has been. The terminus is the last part, and that is the only part a rider boarding
 * it cares about.
 *
 * The separator is a slash with spaces around it. "Dublin/Pleasanton", "Pittsburg/Bay Point" and
 * "Berryessa/North San Jose" are single stations whose own names contain a slash, and splitting on
 * a bare "/" would cut every one of them in half.
 */
export function headsignLabel(headsign: string): string {
	const terminus = headsign.split(/\s+\/\s+/).pop()?.trim() || headsign;
	// The Caltrain platform matters to somebody changing trains and to nobody reading a countdown.
	return terminus.replace(AIRPORT, "SFO Airport").replace(/\s*\(Caltrain Transfer Platform\)$/i, "");
}

/**
 * The names too long for the Dynamic Island to hold.
 *
 * Almost nothing needs an entry: the widget shrinks an over-long lead rather than clipping it, so
 * GLEN PARK and MACARTHUR fit as they are said. These two came out too small to read at a glance
 * on a real phone.
 */
const SHORT_STATIONS: Record<string, string> = {
	"Civic Center": "CIVIC CTR",
	Rockridge: "ROCK",
};

/**
 * A name at island width, upper case.
 *
 * The whole name unless it is listed above. Dropped vowels are the kind of thing you can read once
 * you already know what it says, which is not the situation on a lock screen.
 */
export function shortLabel(name: string): string {
	const label = stationLabel(name);
	return SHORT_STATIONS[label] ?? label.toUpperCase();
}
